import { op } from "libs/transaction";
import { createAction } from "./action";
import { generateRecordId, getPointer, InboxSubsectionTagRecord, SpecialTagTypeEnum } from "libs/schema";
import { ParsedToken } from "libs/searchQueryParser";
import { authorizeUserCanUpdateInbox } from "./authorizers/inboxAuthorizers";

/* -------------------------------------------------------------------------------------------------
 *  updateInboxSection
 * -------------------------------------------------------------------------------------------------
 */

type UpdateInboxSectionProps = {
  inboxSectionId: string;
  subsections: Array<{
    id: string;
    name: string;
    query: ParsedToken[];
  }>;
  ownerOrganizationId: string;
};

export const updateInboxSection = createAction<UpdateInboxSectionProps>("inboxSection.updateInboxSection", {
  operations: async (props, context) => {
    const { inboxSectionId, subsections, ownerOrganizationId } = props;
    const { loader } = context.environment;

    const pointer = getPointer("tag", inboxSectionId);

    const [_prevSubsections] = await loader.getInboxSubsections({
      currentUserId: context.currentUserId,
      inboxSectionId: inboxSectionId,
    });

    const prevSubsections = _prevSubsections as InboxSubsectionTagRecord[];

    const deleteSubsections = prevSubsections.filter(
      (prevSubsection) => !subsections.some((subsection) => prevSubsection.id === subsection.id),
    );

    return [
      op.update(pointer, "data", { is_reindexing: true }),
      ...subsections.map((subsection, index) => {
        const subsectionPointer = getPointer("tag", subsection.id);

        return op.upsert(subsectionPointer, {
          onCreate: [
            op.set("tag", {
              id: subsection.id,
              type: SpecialTagTypeEnum.INBOX_SUBSECTION,
              name: subsection.name,
              description: null,
              icon: null,
              owner_organization_id: ownerOrganizationId,
              archived_at: null,
              data: {
                user_id: context.currentUserId,
                inbox_section_id: inboxSectionId,
                order: index,
                parsed_query: subsection.query,
              } satisfies InboxSubsectionTagRecord["data"],
            }),
            op.set("tag_user_member", {
              id: generateRecordId("tag_user_member", {
                tag_id: subsection.id,
                user_id: context.currentUserId,
              }),
              tag_id: subsection.id,
              tag_type: SpecialTagTypeEnum.INBOX_SUBSECTION,
              user_id: context.currentUserId,
              creator_user_id: context.currentUserId,
              owner_organization_id: ownerOrganizationId,
            }),
          ],
          onUpdate: [
            op.update(subsectionPointer, { name: subsection.name }),
            op.update(subsectionPointer, "data", {
              user_id: context.currentUserId,
              inbox_section_id: inboxSectionId,
              order: index,
              parsed_query: subsection.query,
            } satisfies InboxSubsectionTagRecord["data"]),
          ],
        });
      }),
      // Note that we're intentionally not deleting the tag_user_member records which
      // link the current user to this subsection tag. This is to preserve our ability
      // to authorize the current user to read/modify this subsection tag (e.g. if, in
      // the future, the user wanted to undelete the subsection tag--which we don't
      // currently support).
      ...deleteSubsections.map((subsection) => op.delete("tag", subsection)),
    ];
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateInbox({
      inboxSectionId: props.inboxSectionId,
      inboxSubsectionIds: props.subsections.map((s) => s.id),
      context,
    });
  },
});

export const actions = {
  updateInboxSection,
};
